import { useEffect, useState } from "react";
import "./visitsOverView.scss";
import { Table, Button, message } from "antd";
import Header from "./Header";
import previewIcon from "./../../../../Assets/svg/table-action-preview.svg";

import PreviewTableModal from "./PreviewTableModal";
import { httpService } from "../../../../services/httpService.service";
import { RightOutlined, LeftOutlined, ExportOutlined } from "@ant-design/icons";

const VisitsOverView = () => {
  const [data, setData] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);
  const [modalVisable, setModalVisable] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [totalTargetVisits, setTotalTargetVisits] = useState(0);
  const [actualVisitsExpanded, setActualVisitsExpanded] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const isGuestShare = process.env.REACT_APP_API_IS_GUEST;

  const fetchData = (projectIds = null) => {
    const contractId = localStorage.getItem("currentContractId");

    let url = `/api/Site/status-counts?id=${contractId}`;

    if (projectIds) {
      projectIds?.map((id) => {
        url += `&projectIds=${id}`;
      });
    }

    setLoading(true);
    httpService.get(
      url,
      (res) => {
        setData(res.data);
        const arr = res.data?.siteVisitStatusCounts || [];
        const sumTargets = arr.reduce(
          (sum, p) => sum + (p.targetVisit || 0),
          0
        );
        setTotalTargetVisits(sumTargets);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchData();
    const onResize = () => setIsLargeScreen(window.innerWidth > 576);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (selectedProjects) {
      fetchData(selectedProjects);
      return;
    }
    fetchData();
  }, [selectedProjects]);

  const handleTogglePreviewModal = (projectId) => {
    setSelectedRecordId(projectId);
    setModalVisable(true);
  };

  const toggleActualVisits = () => {
    setActualVisitsExpanded((prev) => !prev);
  };

  const handleExport = () => {
    const contractId = localStorage.getItem("currentContractId");

    if (!contractId || selectedRowKeys.length === 0) {
      message.warning("Please select at least one row to export.");
      return;
    }

    httpService.post(
      "/api/Site/status-counts/export",
      {
        contractId,
        projectIds: selectedRowKeys,
      },
      (data) => {
        const contractName = localStorage.getItem("currentContractName");
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${contractName || "Contract"}_VisitsOverview.xlsx`
        );
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      },
      (err) => {
        console.error(err);
        message.error("Export failed.");
      },
      {
        responseType: "blob",
      }
    );
  };

  const columns = [
    {
      title: "Round",
      dataIndex: "projectName",
      key: "projectName",
      sorter: (a, b) =>
        (a.projectName || "").localeCompare(b.projectName || ""),
    },
    {
      title: "Target Visits",
      dataIndex: "targetVisit",
      key: "targetVisit",
      className: "grayColumn text-center",
      sorter: (a, b) => (a.targetVisit || 0) - (b.targetVisit || 0),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={toggleActualVisits}
          aria-label="Toggle Actual Visits Details"
        >
          Actual Visits
          {actualVisitsExpanded ? (
            <LeftOutlined style={{ marginLeft: 8 }} />
          ) : (
            <RightOutlined style={{ marginLeft: 8 }} />
          )}
        </div>
      ),
      dataIndex: "total",
      key: "total",
      className: "darkBlueColumn text-center",
    },
    ...(actualVisitsExpanded
      ? [
          {
            title: "Planning",
            dataIndex: "planning",
            key: "planning",
            className: "lightBlueColumn text-center",
            sorter: (a, b) => (a.planning || 0) - (b.planning || 0),
          },
          {
            title: "Implementation",
            dataIndex: "implementation",
            key: "implementation",
            className: "lightBlueColumn text-center",
            sorter: (a, b) => (a.implementation || 0) - (b.implementation || 0),
          },
          {
            title: "Reporting",
            dataIndex: "reporting",
            key: "reporting",
            className: "lightBlueColumn text-center",
            sorter: (a, b) => (a.reporting || 0) - (b.reporting || 0),
          },
          {
            title: "Completed",
            dataIndex: "completed",
            key: "completed",
            className: "lightBlueColumn text-center",
            sorter: (a, b) => (a.completed || 0) - (b.completed || 0),
          },
        ]
      : []),
    {
      title: "Target vs Actual",
      dataIndex: "targetVsActual",
      key: "targetVsActual",
      className: "greenColumn text-center",
      sorter: (a, b) => (a.targetVsActual || 0) - (b.targetVsActual || 0),
    },
    {
      title: "Moved to another round",
      dataIndex: "movedToAnotherRound",
      key: "movedToAnotherRound",
      className: "orangeColumn text-center",
      sorter: (a, b) =>
        (a.movedToAnotherRound || 0) - (b.movedToAnotherRound || 0),
    },
    ...(!isGuestShare
      ? [
          {
            title: "",
            key: "action",
            width: 120,
            fixed:
              isLargeScreen && data?.siteVisitStatusCounts?.length > 0
                ? "right"
                : undefined,
            render: (_, record) => (
              <div className="actionDiv">
                <img
                  src={previewIcon}
                  onClick={() => handleTogglePreviewModal(record.projectId)}
                  style={{ cursor: "pointer" }}
                  alt="Preview"
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="tabContainer">
        <Header
          actualVisits={data?.actualVisitOfContract || 0}
          totalTargetVisits={totalTargetVisits}
          selectedProjects={selectedProjects}
          setSelectedProjects={setSelectedProjects}
        />
        <Button
          type="primary"
          icon={<ExportOutlined style={{ marginRight: 8 }} />}
          onClick={handleExport}
          disabled={selectedRowKeys.length === 0}
        >
          Export to Excel
        </Button>
        <Table
          rowKey="projectId"
          className="tableTPM"
          scroll={{ x: "max-content" }}
          columns={columns}
          loading={loading}
          dataSource={data?.siteVisitStatusCounts || []}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          style={{ width: "100%" }}
        />
      </div>

      <PreviewTableModal
        visible={modalVisable}
        onCancel={() => setModalVisable(false)}
        roundId={selectedRecordId}
      />
    </>
  );
};

export default VisitsOverView;
